import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import {
  Receipt,
  FileCheck2,
  ChevronDown,
  ChevronRight,
  Banknote,
  FileText,
  Download,
  Loader2,
} from 'lucide-react';
import { siteConfig } from '@sighthost/config';
import { pdf } from '@react-pdf/renderer';
import { InvoicePdfDocument } from '@/domains/billing/components/InvoicePdfDocument';
import {
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  UniversalEmptyState,
  UniversalLoadingState,
  useToast,
  Modal,
  ModalBody,
  ModalFooter,
  Input,
  FormField,
  BrandLogo,
} from '@sighthost/ui';
import {
  adminInvoiceService,
  type AdminInvoiceRow,
  type AdminInvoiceLine,
  type AdminPaymentRow,
  type AdminProofOfPayment,
  type InvoiceLifecycleState,
} from '@/domains/billing/services/adminInvoiceService';
import { adminPaymentService } from '@/domains/billing/services/adminPaymentService';
import {
  customerStatementService,
  AGING_BUCKET_LAYOUT,
  DEFAULT_PERIODS,
  type AgingBuckets,
  type StatementArchetype,
  type StatementBundle,
  type BalanceForwardStatement,
  type OpenItemStatement,
  type ActivityStatement,
  type AgingBucketKey,
} from '@/domains/billing/services/customerStatementService';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';
import { CopyPayLink } from '@/shared/components/billing/CopyPayLink';
import { buildPayUrl } from '@/shared/lib/payLinkMessage';
import type { CustomerDetailContext } from './customerDetailTypes';
import { PaymentArrangementPanel } from './PaymentArrangementPanel';
import { formatZAR } from '@/shared/utils/format';

// Must match payment-record-manual's PAYABLE_STATES, which in turn mirrors the
// invoice_state_transition_guard trigger. 'pending_payment' added 2026-07-27 —
// without it the Record-payment button never rendered for a quotation (every
// quotation lives in pending_payment), so a paid-by-EFT quote could not be
// settled from this screen at all.
const PAYABLE_STATES: ReadonlySet<InvoiceLifecycleState> = new Set([
  'pending_payment',
  'unpaid',
  'grace',
  'suspended',
]);

// Money actually owed, for seeding a payment arrangement. Deliberately NOT
// PAYABLE_STATES: that set includes 'pending_payment', which is where every
// QUOTATION lives. A quotation is an offer, not a debt, and seeding an
// arrangement with one would have staff promising a client's money against
// something they never agreed to buy.
const OPEN_INVOICE_STATES: ReadonlySet<InvoiceLifecycleState> = new Set([
  'unpaid',
  'grace',
  'suspended',
]);

const STATE_TONE: Record<InvoiceLifecycleState, StatusTone> = {
  draft: 'draft',
  pending_payment: 'pending',
  unpaid: 'pending',
  grace: 'pending',
  paid: 'paid',
  suspended: 'overdue',
  refunded: 'info',
  cancelled: 'draft',
};

/** "2026-07-31" or an ISO timestamp -> "31 July 2026" for customer-facing copy. */
function formatLongDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function CustomerBillingTab() {
  /**
   * NO FILTER BAR HERE (2026-09-13). This is a TAB inside one customer's record
   * — already scoped to that customer, which IS the filter. The cross-customer
   * equivalents (AdminInvoicesPage, AdminPaymentsPage, AdminCreditNotesPage)
   * are fully filtered. Revisit only if one customer's book routinely outgrows
   * a screen; today the largest is ~20 documents.
   */
  const { customer } = useOutletContext<CustomerDetailContext>();
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<AdminInvoiceRow[]>([]);
  const [payments, setPayments] = useState<AdminPaymentRow[]>([]);
  const [proofs, setProofs] = useState<AdminProofOfPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [recordPaymentTarget, setRecordPaymentTarget] = useState<AdminInvoiceRow | null>(
    null
  );
  const [viewInvoiceTarget, setViewInvoiceTarget] = useState<AdminInvoiceRow | null>(
    null
  );
  // Which proof-of-payment decision is in flight — drives per-button spinners
  // and disables the whole proof row so a double-click can't double-process
  // a money mutation (invoice → paid).
  const [decidingProof, setDecidingProof] = useState<
    { id: string; decision: 'verify' | 'reject' } | null
  >(null);
  // Invoice ids where the &autopay=1 link would succeed. Sourced from
  // v_admin_invoices_all -> public.invoice_autopay_eligible(), never derived
  // client-side: offering auto-pay to a customer who already holds a live
  // PayFast mandate would enrol them a second time.
  const [autopayIds, setAutopayIds] = useState<Set<string>>(new Set());

  function refresh() {
    setLoading(true);
    setError(null);
    Promise.all([
      adminInvoiceService.listForCustomer(customer.customer_id),
      adminInvoiceService.listPaymentsForCustomer(customer.customer_id),
      adminInvoiceService.listPendingProofsForCustomer(customer.customer_id),
      adminInvoiceService.listAutopayEligibleIds(customer.customer_id),
    ])
      .then(([inv, pay, pp, autopay]) => {
        setInvoices(inv);
        setPayments(pay);
        setProofs(pp);
        setAutopayIds(autopay);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      adminInvoiceService.listForCustomer(customer.customer_id),
      adminInvoiceService.listPaymentsForCustomer(customer.customer_id),
      adminInvoiceService.listPendingProofsForCustomer(customer.customer_id),
      adminInvoiceService.listAutopayEligibleIds(customer.customer_id),
    ])
      .then(([inv, pay, pp, autopay]) => {
        if (!cancelled) {
          setInvoices(inv);
          setPayments(pay);
          setProofs(pp);
          setAutopayIds(autopay);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customer.customer_id]);

  async function resendInvoice(id: string) {
    try {
      await adminInvoiceService.resend(id);
      toast({
        tone: 'success',
        title: 'Invoice email queued',
        body: 'The notification runner will send within ~1 minute.',
      });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not resend',
        body: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function decideProof(
    proof: AdminProofOfPayment,
    decision: 'verify' | 'reject'
  ) {
    if (decidingProof) return;
    setDecidingProof({ id: proof.id, decision });
    try {
      await adminInvoiceService.verifyProofOfPayment(proof.id, decision);
      toast({
        tone: 'success',
        title: decision === 'verify' ? 'Proof verified' : 'Proof rejected',
        body:
          decision === 'verify'
            ? 'Invoice marked paid; unsuspend job enqueued if applicable.'
            : 'Customer notified.',
      });
      refresh();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Could not process proof',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setDecidingProof(null);
    }
  }

  if (loading) return <UniversalLoadingState variant="rows" count={5} />;
  if (error) {
    return (
      <NotificationCard
        tone="danger"
        title="Could not load billing"
        body={error.message}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment arrangements. Above the statement deliberately: whether this
          customer is under an agreed hold changes how you read everything
          below it, and it is the first thing to check before chasing a debt. */}
      <PaymentArrangementPanel
        customerId={customer.customer_id}
        customerName={customer.full_name}
        outstanding={invoices
          .filter((i) => i.lifecycle_state !== null && OPEN_INVOICE_STATES.has(i.lifecycle_state))
          .reduce((s, i) => s + Number(i.total ?? 0), 0)}
      />

      {/* Statement (3 archetypes + aging) — copies hindsight-accounting-suite */}
      <CustomerStatementCard invoices={invoices} payments={payments} />

      {/* Pending proofs of payment */}
      {proofs.length > 0 && (
        <Card interactive={false}>
          <header className="border-b border-border px-6 py-3">
            <h3 className="text-sm font-semibold text-foreground">
              Pending proofs of payment ({proofs.length})
            </h3>
          </header>
          <div className="divide-y divide-border">
            {proofs.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center gap-3 px-6 py-4 text-sm"
              >
                <FileCheck2 className="h-4 w-4 text-warning" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">
                    Invoice {p.invoice_id.slice(0, 8)}…
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Uploaded {new Date(p.uploaded_at).toLocaleString('en-ZA')}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => decideProof(p, 'reject')}
                  loading={decidingProof?.id === p.id && decidingProof.decision === 'reject'}
                  disabled={decidingProof !== null}
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => decideProof(p, 'verify')}
                  loading={decidingProof?.id === p.id && decidingProof.decision === 'verify'}
                  disabled={decidingProof !== null}
                >
                  Verify
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Invoices */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-foreground">
            Invoices ({invoices.length})
          </h3>
        </header>
        {invoices.length === 0 ? (
          <UniversalEmptyState
            icon={Receipt}
            title="No invoices yet"
            description="Issued invoices for this customer will appear here."
          />
        ) : (
          <div className="divide-y divide-border">
            {invoices.map((inv) => (
              <InvoiceRow
                key={inv.id}
                invoice={inv}
                customerName={customer.full_name}
                autopayEligible={autopayIds.has(inv.id)}
                onResend={resendInvoice}
                onRecordPayment={setRecordPaymentTarget}
                onView={setViewInvoiceTarget}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Payments */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-foreground">
            Payments ({payments.length})
          </h3>
        </header>
        {payments.length === 0 ? (
          <UniversalEmptyState
            icon={Receipt}
            title="No payments yet"
            description="Captured payments for this customer will appear here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-6 py-2">Date</th>
                  <th className="px-6 py-2">Gateway</th>
                  <th className="px-6 py-2">Status</th>
                  <th className="px-6 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-6 py-2 text-foreground">
                      {p.paid_at
                        ? new Date(p.paid_at).toLocaleDateString('en-ZA')
                        : '—'}
                    </td>
                    <td className="px-6 py-2 text-muted-foreground">{p.gateway}</td>
                    <td className="px-6 py-2">
                      <span className="text-xs uppercase tracking-wider">
                        {p.status}
                      </span>
                    </td>
                    <td className="px-6 py-2 text-right font-medium tabular-nums">
                      {formatZAR(p.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {recordPaymentTarget && (
        <RecordManualPaymentModal
          invoice={recordPaymentTarget}
          onClose={() => setRecordPaymentTarget(null)}
          onRecorded={() => {
            setRecordPaymentTarget(null);
            refresh();
          }}
        />
      )}

      {viewInvoiceTarget && (
        <InvoiceDetailModal
          invoice={viewInvoiceTarget}
          customer={customer}
          payments={payments.filter((p) => p.invoice_id === viewInvoiceTarget.id)}
          onClose={() => setViewInvoiceTarget(null)}
        />
      )}
    </div>
  );
}

function InvoiceRow({
  invoice,
  customerName,
  autopayEligible,
  onResend,
  onRecordPayment,
  onView,
}: {
  invoice: AdminInvoiceRow;
  customerName: string;
  autopayEligible: boolean;
  onResend: (id: string) => Promise<void>;
  onRecordPayment: (invoice: AdminInvoiceRow) => void;
  onView: (invoice: AdminInvoiceRow) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [lines, setLines] = useState<AdminInvoiceLine[] | null>(null);
  const [loadingLines, setLoadingLines] = useState(false);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  async function handleResend() {
    if (resending) return;
    setResending(true);
    try {
      await onResend(invoice.id);
    } finally {
      setResending(false);
    }
  }

  async function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && lines === null) {
      setLoadingLines(true);
      try {
        const data = await adminInvoiceService.getLines(invoice.id);
        setLines(data);
        setLinesError(null);
      } catch (err) {
        // Same defect as the document modal (F-05), second location: this used
        // to `setLines([])`, and the render below prints "No line items." for
        // an empty array — so a failed fetch was indistinguishable from an
        // invoice that genuinely has none. Keep `lines` null and say what
        // happened.
        setLines(null);
        setLinesError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoadingLines(false);
      }
    }
  }

  const stateKey: InvoiceLifecycleState =
    (invoice.lifecycle_state as InvoiceLifecycleState | null) ??
    (invoice.status as InvoiceLifecycleState);
  const tone = STATE_TONE[stateKey] ?? 'draft';
  const canRecordPayment = PAYABLE_STATES.has(stateKey);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 text-sm">
        <button
          type="button"
          onClick={toggle}
          className="text-muted-foreground hover:text-foreground"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <span className="text-xs text-foreground">{invoice.number}</span>
        {invoice.doc_type === 'quotation' && (
          <span
            className="inline-flex items-center rounded bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-warning-dark"
            title={
              invoice.quote_expires_at
                ? `Quotation — valid until ${new Date(invoice.quote_expires_at).toLocaleDateString('en-ZA')}`
                : 'Quotation — converts to an invoice when paid'
            }
          >
            Quote
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {invoice.period_start && invoice.period_end
            ? `${invoice.period_start} → ${invoice.period_end}`
            : invoice.issue_date}
        </span>
        <span className="ml-auto font-medium tabular-nums text-foreground">
          {formatZAR(invoice.total)}
        </span>
        <span className="text-xs text-muted-foreground">due {invoice.due_date}</span>
        <StatusBadge tone={tone} dot>
          {stateKey}
        </StatusBadge>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onView(invoice)}
        >
          <FileText className="h-4 w-4" />
          View
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleResend}
          loading={resending}
          disabled={resending}
        >
          Resend
        </Button>
        {canRecordPayment && (
          <Button
            size="sm"
            onClick={() => onRecordPayment(invoice)}
          >
            <Banknote className="h-4 w-4" />
            Record payment
          </Button>
        )}
      </div>
      {expanded && (
        <div className="border-t border-border bg-muted px-12 py-3 text-sm">
          {/* Send a payment link (2026-07-27). Lives in the expanded body rather
              than the row, which already carries View / Resend / Record payment
              and cannot take three more buttons. Shown only while the document
              is actually payable, so a settled invoice offers no dead link.

              The auto-pay variant appears only when
              public.invoice_autopay_eligible() says the &autopay=1 checkout
              would actually succeed — a hosting_renewal on a monthly active
              plan whose account holds no live PayFast mandate. That rule
              mirrors every 409 in hosting-subscription-public-initiate and is
              never re-derived client-side: offering it to an already-subscribed
              customer would enrol them twice, which is a double-charge. */}
          {canRecordPayment && invoice.payment_token && (
            <div className="mb-3 rounded-md border border-border bg-background p-3">
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Send payment link
              </div>
              <CopyPayLink
                payUrl={buildPayUrl(invoice.id, invoice.payment_token)}
                autopayUrl={
                  autopayEligible
                    ? buildPayUrl(invoice.id, invoice.payment_token, { autopay: true })
                    : null
                }
                reference={invoice.number}
                docLabel={invoice.doc_type === 'quotation' ? 'quotation' : 'invoice'}
                amountZar={invoice.total}
                customerName={customerName}
                dueLabel={formatLongDate(
                  invoice.doc_type === 'quotation'
                    ? invoice.quote_expires_at ?? invoice.due_date
                    : invoice.due_date
                )}
              />
            </div>
          )}
          {loadingLines ? (
            <p className="text-muted-foreground">Loading lines…</p>
          ) : linesError !== null ? (
            /* Distinct from "No line items." — that sentence is a FACT about
               the invoice, and must never be used to report a failed fetch. */
            <p className="text-danger-dark">
              Line items could not be loaded — {linesError}
            </p>
          ) : !lines || lines.length === 0 ? (
            <p className="text-muted-foreground">No line items.</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-1.5">Description</th>
                  <th className="py-1.5 text-right">Qty</th>
                  <th className="py-1.5 text-right">Unit</th>
                  <th className="py-1.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-1.5 text-foreground">{l.description}</td>
                    <td className="py-1.5 text-right tabular-nums">{l.quantity}</td>
                    <td className="py-1.5 text-right tabular-nums">
                      {(l.discount_amount ?? 0) > 0 && l.total === 0 ? (
                        <span className="text-foreground-soft line-through">
                          {formatZAR(l.unit_price)}
                        </span>
                      ) : (
                        formatZAR(l.unit_price)
                      )}
                    </td>
                    <td className="py-1.5 text-right tabular-nums font-medium">
                      {(l.discount_amount ?? 0) > 0 && l.total === 0 ? (
                        <span className="text-primary">FREE</span>
                      ) : (
                        formatZAR(l.total)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecordManualPaymentModal({
  invoice,
  onClose,
  onRecorded,
}: {
  invoice: AdminInvoiceRow;
  onClose: () => void;
  onRecorded: (result: { triggered_unsuspend: boolean; invoice_number: string }) => void;
}) {
  const { toast } = useToast();
  const [gateway, setGateway] = useState<'eft_manual' | 'staff_adjustment'>('eft_manual');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState<string>(() => {
    const now = new Date();
    const off = now.getTimezoneOffset() * 60_000;
    return new Date(now.getTime() - off).toISOString().slice(0, 16);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await adminPaymentService.recordManual({
        invoice_id: invoice.id,
        gateway,
        reference: reference.trim() || undefined,
        paid_at: paidAt ? new Date(paidAt).toISOString() : undefined,
      });
      toast({
        tone: 'success',
        title: 'Payment recorded',
        body: result.triggered_unsuspend
          ? `Invoice ${result.invoice_number} marked paid; unsuspend job enqueued.`
          : `Invoice ${result.invoice_number} marked paid.`,
      });
      onRecorded(result);
    } catch (err) {
      if (err instanceof EdgeFunctionError) {
        if (err.code === 'invoice_already_paid') {
          setError('This invoice is already in a terminal state and cannot accept a payment.');
        } else if (err.code === 'invoice_not_issued') {
          setError('Issue this invoice first (draft → unpaid) before recording a payment.');
        } else if (err.code === 'amount_mismatch') {
          setError('Amount must equal the invoice total exactly (AD-13 prepaid model).');
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Record manual payment"
      description={
        <>
          Invoice <span className="font-sans">{invoice.number}</span> ·{' '}
          {formatZAR(invoice.total)}
        </>
      }
    >
      <ModalBody>
        <div className="space-y-4">
          <NotificationCard
            tone="info"
            title="Strict prepaid (AD-13)"
            body="Amount is locked to the invoice total. Partial or over-payments are not accepted by the billing engine."
          />

          <FormField label="Amount (read-only)" htmlFor="amount">
            <Input
              id="amount"
              value={formatZAR(invoice.total)}
              readOnly
              disabled
              className="font-sans"
            />
          </FormField>

          <FormField label="Gateway" htmlFor="gateway" required>
            <select
              id="gateway"
              value={gateway}
              onChange={(e) =>
                setGateway(e.target.value as 'eft_manual' | 'staff_adjustment')
              }
              disabled={submitting}
              className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="eft_manual">EFT (manual capture)</option>
              <option value="staff_adjustment">Staff adjustment</option>
            </select>
          </FormField>

          <FormField
            label="Bank reference / note"
            htmlFor="reference"
            helper="Recorded as payment.gateway_transaction_id and on audit log."
          >
            <Input
              id="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={submitting}
              placeholder="e.g. ABSA REF 12345"
              maxLength={200}
            />
          </FormField>

          <FormField
            label="Paid at"
            htmlFor="paid_at"
            helper="Defaults to now. Cannot be in the future or older than 1 year."
          >
            <Input
              id="paid_at"
              type="datetime-local"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              disabled={submitting}
            />
          </FormField>

          {error && (
            <NotificationCard tone="warning" title="Could not record payment" body={error} />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={submit} loading={submitting} disabled={submitting}>
          {submitting ? 'Recording…' : 'Record payment'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function InvoiceDetailModal({
  invoice,
  customer,
  payments,
  onClose,
}: {
  invoice: AdminInvoiceRow;
  customer: {
    full_name: string;
    email: string | null;
    company: string | null;
    billing_email: string | null;
    billing_address_line1: string | null;
    billing_address_line2: string | null;
    billing_address_city: string | null;
    billing_address_postal_code: string | null;
    billing_address_country: string;
    registration_number: string | null;
    vat_number: string | null;
  };
  payments: AdminPaymentRow[];
  onClose: () => void;
}) {
  const [lines, setLines] = useState<AdminInvoiceLine[] | null>(null);
  const [loadingLines, setLoadingLines] = useState(true);
  const { toast } = useToast();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  /**
   * F-05. This used to be `.catch(() => setLines([]))`, which rendered the
   * document with NO line items while still showing its total — and this is
   * the modal staff print and hand to customers.
   *
   * That failure was indistinguishable from a genuinely line-less invoice,
   * which is not a hypothetical shape here: every historical hosting renewal
   * carried `line_count = 0` until the 2026-05-21 billing-cron fix, and four
   * of them needed a backfill migration. A reader had no way to tell "this
   * invoice has no lines" from "we could not load them".
   *
   * Holding the error is only half the fix; the other half is refusing to
   * export. An invoice you cannot render is a reason not to send one.
   */
  const [linesError, setLinesError] = useState<string | null>(null);

  async function downloadPdf() {
    if (!lines) return;
    setDownloadingPdf(true);
    try {
      const logoUrl = `${window.location.origin}/images/logo.png`;
      const blob = await pdf(
        <InvoicePdfDocument
          invoice={invoice}
          lines={lines}
          customer={customer}
          payments={payments}
          logoUrl={logoUrl}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `INVOICE_${invoice.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Defer revoke so iOS Safari has time to start the download
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      console.error('PDF generation failed', err);
      // F-27, last site. A native alert() on a document staff hand to
      // customers: unstyled, blocking, and it threw the error away entirely so
      // nobody could say WHY the PDF failed. The toast carries the reason.
      toast({
        tone: 'danger',
        title: 'Could not generate the PDF',
        body:
          (err instanceof Error ? err.message : String(err)) +
          ' — use your browser’s Print to PDF instead, or contact support.',
      });
    } finally {
      setDownloadingPdf(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    adminInvoiceService
      .getLines(invoice.id)
      .then((data) => {
        if (!cancelled) {
          setLines(data);
          setLinesError(null);
          setLoadingLines(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // `lines` stays null — NOT [] — so nothing downstream can mistake a
          // failed load for an empty invoice.
          setLines(null);
          setLinesError(err instanceof Error ? err.message : String(err));
          setLoadingLines(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [invoice.id]);

  const isPaid = invoice.lifecycle_state === 'paid';
  const isQuotation = invoice.doc_type === 'quotation';
  const docTitle = isQuotation
    ? 'QUOTATION'
    : siteConfig.vatRegistered
      ? 'TAX INVOICE'
      : 'INVOICE';
  const billTo = customer.billing_email ?? customer.email ?? '—';
  const company = customer.company?.trim();
  const addressLines = [
    customer.billing_address_line1,
    customer.billing_address_line2,
    [customer.billing_address_city, customer.billing_address_postal_code]
      .filter(Boolean)
      .join(' '),
    customer.billing_address_country,
  ].filter((l): l is string => !!l && l.trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-scrim/40 p-4 backdrop-blur-sm print:static print:bg-transparent print:p-0 print:backdrop-blur-none"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-document shadow-modal print:max-h-none print:max-w-none print:rounded-none print:shadow-none"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Invoice ${invoice.number}`}
      >
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-3 print:hidden">
          <h3 className="text-sm font-semibold text-foreground">
            {isQuotation ? 'Quotation' : 'Invoice'} {invoice.number}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {/* Disabled while the lines failed to load. The PDF renderer would
                happily produce a document with a total and no line items —
                which is worse than no PDF, because it looks finished. */}
            <Button
              size="sm"
              onClick={downloadPdf}
              disabled={downloadingPdf || loadingLines || linesError !== null}
              loading={downloadingPdf}
              title={
                linesError
                  ? 'Line items could not be loaded — exporting would produce an incomplete document.'
                  : undefined
              }
            >
              {downloadingPdf ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {downloadingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-document text-document-ink">

          {/* ProfessionalTemplate look-and-feel — mirrors hindsight-accounting-suite */}
          <div className="mx-auto max-w-5xl">
            <div className="p-8 print:p-6">

              {/* HEADER — logo (left) / document name + number (right) */}
              <div className="mb-8 flex items-start justify-between">
                <div className="space-y-1">
                  <BrandLogo variant="tagline" tone="colour" className="mb-2 h-28" />
                </div>
                <div className="text-right">
                  <h1 className="mb-1 text-2xl font-bold text-document-ink">
                    {docTitle}
                  </h1>
                  <p className="text-sm text-document-ink-soft">{invoice.number}</p>
                  {isQuotation && invoice.quote_expires_at && (
                    <p className="text-xs text-warning-dark">
                      Valid until{' '}
                      {new Date(invoice.quote_expires_at).toLocaleDateString('en-ZA')}
                    </p>
                  )}
                  {!isQuotation && invoice.quote_number && (
                    <p className="text-xs text-document-ink-soft">
                      Ref: quotation {invoice.quote_number}
                    </p>
                  )}
                  {invoice.quote_metadata?.kind === 'studio_deposit' && (
                    <div className="mt-2 rounded border border-document-rule bg-document-zebra p-2 text-left text-xs text-document-ink-soft">
                      <p>Studio Starter: {formatZAR(Number(invoice.quote_metadata.total_cents ?? 0) / 100)}</p>
                      <p>Deposit now: ({formatZAR(Number(invoice.quote_metadata.deposit_cents ?? 0) / 100)})</p>
                      <p>
                        Plan: {invoice.quote_metadata.plan_months} ×{' '}
                        {formatZAR(Number(invoice.quote_metadata.installment_cents ?? 0) / 100)}/month
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ADDRESS — Company (left) / Bill To + REF (right) */}
              <div className="mb-6 grid grid-cols-2 gap-6">
                <div>
                  <h3 className="mb-1 text-xs font-semibold text-document-ink-soft">
                    Company:
                  </h3>
                  <div className="space-y-0 text-xs leading-tight text-document-ink">
                    <p className="font-bold">{siteConfig.legalName}</p>
                    <p className="whitespace-pre-line">
                      {siteConfig.address.street}
                    </p>
                    <p>{siteConfig.address.locality}</p>
                    <p>
                      {siteConfig.address.city}{' '}
                      {siteConfig.address.postalCode}
                    </p>
                    <p>{siteConfig.address.countryName}</p>
                    <p>{siteConfig.phoneDisplay}</p>
                    <p>{siteConfig.billingEmail}</p>
                    <p>Reg: {siteConfig.registration}</p>
                    {siteConfig.vatRegistered && siteConfig.vatNumber && (
                      <p>VAT: {siteConfig.vatNumber}</p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-1 flex items-start justify-between">
                    <h3 className="text-xs font-semibold text-document-ink-soft">
                      Bill To:
                    </h3>
                    <div className="text-right">
                      <p className="text-xs text-document-ink-soft">
                        REF:{' '}
                        <span className="font-semibold text-document-ink">
                          {invoice.number}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-0 text-xs leading-tight text-document-ink">
                    <p className="font-bold">
                      {company ?? customer.full_name}
                    </p>
                    {company && (
                      <p className="font-semibold text-document-ink-soft">
                        Att: {customer.full_name}
                      </p>
                    )}
                    {addressLines.map((l, i) => (
                      <p key={i}>{l}</p>
                    ))}
                    <p>{billTo}</p>
                    {customer.registration_number && (
                      <p>Reg: {customer.registration_number}</p>
                    )}
                    {customer.vat_number && (
                      <p>VAT: {customer.vat_number}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* DATES — Issue/Due (left) / Payment Terms (right) */}
              <div className="mb-4 grid grid-cols-2 gap-6">
                <div>
                  <div className="space-y-0 text-xs leading-tight text-document-ink-soft">
                    <p>Issue Date:</p>
                    <p className="font-semibold text-document-ink">
                      {invoice.issue_date}
                    </p>
                    <p>Due Date:</p>
                    <p className="font-semibold text-document-ink">
                      {invoice.due_date}
                    </p>
                    {invoice.period_start && invoice.period_end && (
                      <>
                        <p className="mt-2">Service Period:</p>
                        <p className="font-semibold text-document-ink">
                          {invoice.period_start} → {invoice.period_end}
                        </p>
                      </>
                    )}
                    {invoice.paid_at && (
                      <>
                        <p className="mt-2">Paid On:</p>
                        <p className="font-semibold text-success-dark">
                          {new Date(invoice.paid_at).toLocaleDateString(
                            'en-ZA'
                          )}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="space-y-0 text-xs leading-tight text-document-ink-soft">
                    <p>Payment Terms:</p>
                    <p className="font-semibold text-document-ink">
                      Strict prepaid (AD-13)
                    </p>
                    <p className="mt-2">Status:</p>
                    <p className="font-semibold uppercase tracking-wider text-document-ink">
                      {invoice.lifecycle_state ?? invoice.status}
                    </p>
                  </div>
                </div>
              </div>

              {/* LINE ITEMS — 6 cols, fixed widths, 12-row padding */}
              <div className="mb-6 overflow-x-auto">
                <table className="w-full table-fixed text-[10px]">
                  <colgroup>
                    <col className="w-16" />
                    <col className="w-auto" />
                    <col className="w-12" />
                    <col className="w-24" />
                    <col className="w-12" />
                    <col className="w-24" />
                  </colgroup>
                  <thead>
                    <tr className="bg-document-zebra">
                      <th className="px-1 py-1.5 text-left text-xs">Code</th>
                      <th className="px-2 py-1.5 text-left text-xs">
                        Description
                      </th>
                      <th className="px-1 py-1.5 text-center text-xs">Qty</th>
                      <th className="px-1 py-1.5 text-center text-xs">
                        Unit Price
                      </th>
                      <th className="px-1 py-1.5 text-center text-xs">VAT</th>
                      <th className="px-1 py-1.5 text-right text-xs">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingLines && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-2 py-2 text-center text-document-ink-soft"
                        >
                          Loading line items…
                        </td>
                      </tr>
                    )}
                    {/* The failure says so IN THE DOCUMENT, in the place the
                        line items would have been. Rendering nothing here is
                        what made a failed load look like a settled invoice
                        with no lines. Deliberately not `print:hidden` — if
                        someone prints this anyway, the paper must carry the
                        warning too. */}
                    {!loadingLines && linesError !== null && (
                      <tr>
                        <td colSpan={6} className="px-2 py-3">
                          <p className="font-semibold text-danger-dark">
                            Line items could not be loaded.
                          </p>
                          <p className="mt-0.5 text-document-ink-soft">
                            This document is incomplete — the totals below are from
                            the invoice record, but the items making them up are
                            missing. Do not send it to the customer. {linesError}
                          </p>
                        </td>
                      </tr>
                    )}
                    {!loadingLines &&
                      lines?.map((l, index) => (
                        <tr
                          key={l.id}
                          className={
                            index % 2 === 0 ? 'bg-document' : 'bg-document-zebra'
                          }
                        >
                          <td className="truncate px-1 py-1.5">
                            {l.target_ref ?? '-'}
                          </td>
                          <td className="whitespace-pre-line px-2 py-1.5">
                            {l.description}
                            {l.period_start && l.period_end && (
                              <div className="text-[9px] text-document-ink-soft">
                                {l.period_start} → {l.period_end}
                              </div>
                            )}
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            {l.quantity.toLocaleString()}
                          </td>
                          <td
                            className={
                              (l.discount_amount ?? 0) > 0 && l.total === 0
                                ? 'px-1 py-1.5 text-center text-document-ink-soft line-through'
                                : 'px-1 py-1.5 text-center'
                            }
                          >
                            {l.unit_price.toLocaleString('en-ZA', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            {Math.round((l.vat_rate ?? 0) * 100)}%
                          </td>
                          <td className="px-1 py-1.5 text-right">
                            {(l.discount_amount ?? 0) > 0 && l.total === 0
                              ? 'FREE'
                              : l.total.toLocaleString('en-ZA', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                          </td>
                        </tr>
                      ))}
                    {/* Pad to a small minimum so a 1-line invoice still has shape,
                        but stays well within a single A4 page. */}
                    {!loadingLines &&
                      lines &&
                      Array.from(
                        { length: Math.max(0, 4 - lines.length) },
                        (_, i) => (
                          <tr
                            key={`empty-${i}`}
                            className={
                              (lines.length + i) % 2 === 0
                                ? 'bg-document'
                                : 'bg-document-zebra'
                            }
                          >
                            <td className="px-1 py-1">&nbsp;</td>
                            <td className="px-2 py-1">&nbsp;</td>
                            <td className="px-1 py-1 text-center">&nbsp;</td>
                            <td className="px-1 py-1 text-center">&nbsp;</td>
                            <td className="px-1 py-1 text-center">&nbsp;</td>
                            <td className="px-1 py-1 text-right">&nbsp;</td>
                          </tr>
                        )
                      )}
                  </tbody>
                </table>
              </div>

              {/* TOTALS — right-aligned, w-48 */}
              <div className="mb-4 border-t border-document-rule pt-4">
                <div className="flex justify-end">
                  <div className="w-48 text-xs">
                    <div className="mb-1 flex justify-between">
                      <span className="text-document-ink-soft">Subtotal:</span>
                      <span className="text-document-ink">
                        {formatZAR(invoice.subtotal)}
                      </span>
                    </div>
                    <div className="mb-1 flex justify-between">
                      <span className="text-document-ink-soft">VAT/Tax:</span>
                      <span className="text-document-ink">
                        {formatZAR(invoice.vat_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-bold">
                      <span>Total:</span>
                      <span>{formatZAR(invoice.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* BANKING DETAILS */}
              <div className="mb-4 border-t border-document-rule pt-4">
                <h3 className="mb-1 text-sm font-semibold text-document-ink">
                  Payment Details
                </h3>
                <div className="space-y-0 text-xs leading-tight text-document-ink-soft">
                  <p>Bank: {siteConfig.bank.name}</p>
                  <p>Account name: {siteConfig.bank.accountName}</p>
                  <p>Account: {siteConfig.bank.accountNumber}</p>
                  <p>Branch Code: {siteConfig.bank.branchCode}</p>
                  <p>Account Type: {siteConfig.bank.accountType}</p>
                  {siteConfig.bank.swiftCode && (
                    <p>SWIFT Code: {siteConfig.bank.swiftCode}</p>
                  )}
                  <p>
                    Reference:{' '}
                    <span className="font-semibold">{invoice.number}</span> ·{' '}
                    {siteConfig.bank.referenceHint}
                  </p>
                </div>
              </div>

              {/* PAYMENTS RECEIVED */}
              {payments.length > 0 && (
                <div className="mb-4 border-t border-document-rule pt-4">
                  <h3 className="mb-1 text-sm font-semibold text-document-ink">
                    Payments Received
                  </h3>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-document-ink-soft">
                        <th className="py-1">Date</th>
                        <th className="py-1">Method</th>
                        <th className="py-1">Reference</th>
                        <th className="py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} className="border-t border-document-rule">
                          <td className="py-1 tabular-nums text-document-ink">
                            {p.paid_at
                              ? new Date(p.paid_at).toLocaleDateString('en-ZA')
                              : '—'}
                          </td>
                          <td className="py-1 text-document-ink-soft">{p.gateway}</td>
                          <td className="py-1 text-[10px] text-document-ink-soft">
                            {p.gateway_transaction_id ?? '—'}
                          </td>
                          <td className="py-1 text-right tabular-nums text-document-ink">
                            {formatZAR(p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* FOOTER */}
              <div className="border-t border-document-rule pt-2 text-center text-document-ink-soft">
                <p className="mb-0.5 text-[8px] leading-none">
                  {isPaid
                    ? 'Payment received in full. Thank you for your business!'
                    : 'Thank you for your business!'}
                </p>
                <div className="mb-0.5 text-[8px] leading-none">
                  <span>Company Registration: {siteConfig.registration}</span>
                  {siteConfig.vatRegistered && siteConfig.vatNumber && (
                    <span className="ml-2">
                      VAT Number: {siteConfig.vatNumber}
                    </span>
                  )}
                </div>
                <p className="text-[8px] leading-none">
                  © {new Date().getFullYear()} {siteConfig.legalName}. All
                  rights reserved.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// ============================================================================
// Statement card — three archetypes + aging strip (copies hindsight pattern)
// ============================================================================

type PeriodKey = 'last30' | 'last90' | 'ytd';
const PERIOD_LABELS: Record<PeriodKey, string> = {
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  ytd: 'Year to date',
};

const ARCHETYPE_LABELS: Record<StatementArchetype, string> = {
  balance_forward: 'Balance Forward',
  open_item: 'Open Item',
  activity: 'Activity',
};

/**
 * Ageing-bucket escalation, on semantic tokens.
 *
 * Was five raw Tailwind ramps (slate / amber / orange / red / red-deep) —
 * four hues for what is really TWO states with two intensities each: not yet
 * a problem, warning, danger. "Orange vs red" carried no distinction a reader
 * could name, and none of the five followed a theme.
 *
 * The escalation survives (weak, then strong, within each semantic) and now
 * inverts correctly in dark, where the `-dark` tokens resolve to the light
 * text shades.
 */
const BUCKET_ACCENT: Record<string, string> = {
  slate: 'border-border-strong bg-card-subtle text-foreground-soft',
  amber: 'border-warning/40 bg-warning/10 text-warning-dark',
  orange: 'border-warning bg-warning/20 text-warning-dark',
  red: 'border-danger/40 bg-danger/10 text-danger-dark',
  'red-deep': 'border-danger bg-danger/20 text-danger-dark',
};

function CustomerStatementCard({
  invoices,
  payments,
}: {
  invoices: AdminInvoiceRow[];
  payments: AdminPaymentRow[];
}) {
  const [archetype, setArchetype] = useState<StatementArchetype>('activity');
  const [period, setPeriod] = useState<PeriodKey>('last30');

  const bundle: StatementBundle = (() => {
    const range = DEFAULT_PERIODS[period]();
    // Quotations must not appear on a STATEMENT — an offer is not a
    // transaction, and a quotation in pending_payment carries legacy
    // status='unpaid', so it would be counted as a receivable and would age.
    // Filtered here rather than in listForCustomer, because the invoice LIST
    // above should still show quotations. (2026-07-29 invoice.status sweep.)
    const statementInvoices = invoices.filter((i) => i.doc_type !== 'quotation');
    return customerStatementService.compute(statementInvoices, payments, range);
  })();

  return (
    <Card interactive={false}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-6 py-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Statement</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Switch between Activity (transaction listing), Open Item (outstanding by aging), and Balance Forward (running balance).
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((k) => (
            <Button
              key={k}
              size="sm"
              variant={period === k ? 'primary' : 'outline'}
              onClick={() => setPeriod(k)}
            >
              {PERIOD_LABELS[k]}
            </Button>
          ))}
        </div>
      </header>

      <div className="space-y-4 px-6 py-4">
        <AgingStrip aging={bundle.aging} />

        <div className="flex flex-wrap gap-1 border-b border-border">
          {(['activity', 'open_item', 'balance_forward'] as StatementArchetype[]).map(
            (a) => (
              <button
                key={a}
                type="button"
                onClick={() => setArchetype(a)}
                className={
                  'border-b-2 px-3 py-2 text-sm transition-colors ' +
                  (archetype === a
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground')
                }
              >
                {ARCHETYPE_LABELS[a]}
              </button>
            )
          )}
        </div>

        {archetype === 'activity' && (
          <ActivityView statement={bundle.activity} />
        )}
        {archetype === 'open_item' && (
          <OpenItemView statement={bundle.open_item} />
        )}
        {archetype === 'balance_forward' && (
          <BalanceForwardView statement={bundle.balance_forward} />
        )}
      </div>
    </Card>
  );
}

function AgingStrip({ aging }: { aging: AgingBuckets }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7">
      {AGING_BUCKET_LAYOUT.map((b) => (
        <div
          key={b.key}
          className={
            'rounded border px-3 py-2 text-xs ' + BUCKET_ACCENT[b.accent]
          }
        >
          <div className="font-medium uppercase tracking-wider opacity-70">
            {b.label}
          </div>
          <div className="mt-0.5 font-semibold tabular-nums">
            {formatZAR(aging[b.key as AgingBucketKey])}
          </div>
        </div>
      ))}
      <div className="rounded border border-foreground/30 bg-foreground/5 px-3 py-2 text-xs">
        <div className="font-medium uppercase tracking-wider opacity-70">
          Total
        </div>
        <div className="mt-0.5 font-semibold tabular-nums">
          {formatZAR(aging.total)}
        </div>
      </div>
    </div>
  );
}

function ActivityView({ statement }: { statement: ActivityStatement }) {
  if (statement.events.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No activity in this period.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Date</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2">Reference</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {statement.events.map((e, i) => (
            <tr
              key={`${e.event_at}-${i}`}
              className="border-t border-border"
            >
              <td className="px-3 py-2 tabular-nums text-foreground">
                {e.event_at}
              </td>
              <td className="px-3 py-2">
                <span className="rounded border border-border bg-muted px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                  {e.event_type.replace('_', ' ')}
                </span>
              </td>
              <td className="px-3 py-2 text-foreground">{e.description}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {e.reference ?? '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                <span
                  className={
                    e.signed_amount < 0
                      ? 'text-success-dark'
                      : 'text-foreground'
                  }
                >
                  {e.signed_amount < 0 ? '−' : ''}
                  {formatZAR(Math.abs(e.signed_amount))}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpenItemView({ statement }: { statement: OpenItemStatement }) {
  if (statement.items.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No outstanding invoices. (Strict prepaid: paid invoices clear immediately.)
      </p>
    );
  }
  // Group by bucket
  const byBucket = new Map<AgingBucketKey, OpenItemStatement['items']>();
  for (const layout of AGING_BUCKET_LAYOUT) byBucket.set(layout.key, []);
  for (const item of statement.items) {
    byBucket.get(item.bucket)?.push(item);
  }
  return (
    <div className="space-y-3">
      {AGING_BUCKET_LAYOUT.map((b) => {
        const rows = byBucket.get(b.key) ?? [];
        if (rows.length === 0) return null;
        const subtotal = rows.reduce((s, it) => s + it.outstanding, 0);
        return (
          <div key={b.key} className="rounded border border-border">
            <div className="flex items-center justify-between border-b border-border bg-muted px-3 py-1.5">
              <div className="text-xs font-medium text-foreground">
                {b.label}
              </div>
              <div className="text-xs tabular-nums text-foreground">
                {formatZAR(subtotal)}
              </div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5">Number</th>
                  <th className="px-3 py-1.5">Date</th>
                  <th className="px-3 py-1.5">Due</th>
                  <th className="px-3 py-1.5 text-right">Total</th>
                  <th className="px-3 py-1.5 text-right">Paid</th>
                  <th className="px-3 py-1.5 text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <tr
                    key={it.invoice_id}
                    className="border-t border-border"
                  >
                    <td className="px-3 py-1.5 text-xs">
                      {it.invoice_number}
                    </td>
                    <td className="px-3 py-1.5">{it.invoice_date}</td>
                    <td className="px-3 py-1.5">{it.due_date ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatZAR(it.total)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-success-dark">
                      {it.paid > 0 ? formatZAR(it.paid) : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-danger-dark">
                      {formatZAR(it.outstanding)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BalanceForwardView({
  statement,
}: {
  statement: BalanceForwardStatement;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap justify-between gap-4 rounded border border-border bg-muted px-4 py-2 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Opening balance
          </div>
          <div className="font-semibold tabular-nums">
            {formatZAR(statement.opening_balance)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Closing balance
          </div>
          <div className="font-semibold tabular-nums">
            {formatZAR(statement.closing_balance)}
          </div>
        </div>
      </div>
      {statement.rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No transactions in this period.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2 text-right">Debit</th>
                <th className="px-3 py-2 text-right">Credit</th>
                <th className="px-3 py-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {statement.rows.map((r, i) => (
                <tr
                  key={`${r.date}-${i}`}
                  className="border-t border-border"
                >
                  <td className="px-3 py-2 tabular-nums">{r.date}</td>
                  <td className="px-3 py-2 text-foreground">
                    {r.description}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.reference ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.debit > 0 ? formatZAR(r.debit) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.credit > 0 ? formatZAR(r.credit) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {formatZAR(r.running_balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
